import { computed } from 'vue';
import useUrlParams from '../../../composables/useUrlParams';
import useInvalidatePaginated from '../../../composables/useInvalidatePaginated';

export default function useUrlFilters() {
  const { invalidateQuery } = useInvalidatePaginated();
  const params = useUrlParams();

  /**
   * Url filters
   * Parse url to construct filters object
   *
   * @example
   * filters=main_color%3A%23FFFFFF|%23000000 => { 'main_color': ['#FFFFFF', '#000000'] }
   */
  const urlFilters = computed(() => {
    const raw = params.filters || '';
    return !raw ? {} : raw.split(';').reduce((acc, group) => {
      const [key, values] = group.split(':');
      if (key && values) {
        acc[key] = values.split('|').map((value) => decodeURIComponent(value.trim()));
      }
      return acc;
    }, {});
  });

  /**
   * Url sorts
   * Parse url to construct sorts object
   *
   * @example
   * sort=price:asc => { 'price': 'asc' }
   */
  const urlSorts = computed(() => {
    const raw = params.sorts || '';
    return !raw ? {} : raw.split(';').reduce((acc, group) => {
      const [key, values] = group.split(':');
      if (key && values) {
        acc[key] = decodeURIComponent(values.trim());
      }
      return acc;
    }, {});
  });

  /**
   * Toggle the filter
   * Parse urlFilter object to construct url
   *
   * @example
   * { 'main_color': ['#FFFFFF', '#000000'] } => filters=main_color%3A%23FFFFFF|%23000000
   */
  const toggleFilter = ({ filter, filterValue }) => {
    urlFilters.value[filter.code] = urlFilters.value[filter.code]?.includes(filterValue.code)
      ? urlFilters.value[filter.code].filter((v) => v !== filterValue.code)
      : [...(urlFilters.value[filter.code] || []), filterValue.code];

    if (!urlFilters.value[filter.code]?.length) {
      delete urlFilters.value[filter.code];
    }

    const filtersToUrl = Object.entries(urlFilters.value).map(([key, values]) => `${key}:${values.join('|')}`).join(';');

    delete params.page;

    if (filtersToUrl) {
      params.filters = filtersToUrl;
    } else {
      delete params.filters;
    }

    invalidateQuery(['productListItems']);
  };

  /**
   * Toggle the sort
   * Parse urlSorts object to construct url
   *
   * @example
   * { 'price': 'asc' } => sorts=price%3Aasc
   */
  const toggleSort = ({ sort, sortValue }) => {
    if (urlSorts.value[sort.code] !== sortValue.code) {
      urlSorts.value[sort.code] = sortValue.code;
    } else {
      delete urlSorts.value[sort.code];
    }

    const sortsToUrl = urlSorts.value[sort.code] ? Object.entries(urlSorts.value).map(([key, values]) => `${key}:${values}`).join(';') : '';

    delete params.page;

    if (sortsToUrl) {
      params.sorts = sortsToUrl;
    } else {
      delete params.sorts;
    }

    invalidateQuery(['productListItems']);
  };

  const getQueryParamFilter = () => params.filters || '';

  const getQueryParamFilterSort = () => params.sorts || '';

  return {
    urlFilters, urlSorts, toggleFilter, toggleSort, getQueryParamFilter, getQueryParamFilterSort,
  };
}
